import React from 'react';
import { Table } from 'semantic-ui-react';
import { firstErrorMessage } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import {
  columnName,
  columnNameLink,
  columnRole,
  columnRoleLinkNoFilter,
  columnEmail,
} from '@/constants/fields';
import TableHeader from '@/components/table/TableHeader';
import TableWrapper from '@/components/table/TableWrapper';
import NoRowsWithPagination from '@/components/table/NoRowsWithPagination';
import { fitToPage } from '@/hooks/paging';
import SubscribersFilters from './SubscribersFilters';
import { defaultEvent } from '../helper';
import SubscribersPanelHeader from './SubscribersPanelHeader';
import SubscriptionEventLabel from './SubscriptionEventLabel';
import SubscribersTableColumns from './SubscribersTableColumns';
import SubscribersTableRows from './SubscribersTableRows';
import Selectors from '../hooks/selectors';
import Events from '../hooks/events';

const buildColumns = ({ allowUserLink, allowRoleLink }) => {
  const columns = [columnEmail];
  columns.push(allowUserLink ? columnNameLink : columnName);
  columns.push(allowRoleLink ? columnRoleLinkNoFilter : columnRole);

  return columns;
};

const SubscribersTable = ({ category, code }) => {
  const event = defaultEvent(category);
  const state = Selectors.useSubscribersTable({ category, code, event });
  const fetchData = Events.useFetchSubscribers(category, { code });
  const { items, pageNumber } = fitToPage(state.subscribers, state.currentPage, state.pageSize);
  const ref = React.useRef(null);
  const columns = buildColumns(state);

  return (
    <div className="ReactTable" ref={ref}>
      <TableWrapper sticky celled className="aridhia-subscribers-table">
        <Table.Header fullWidth>
          <TableHeader
            isLoading={state.readStatus === states.PENDING && !state.name}
            error={firstErrorMessage(state.readErrors)}
            numberOfColumns={columns.length}
          >
            {code && (
            <SubscribersPanelHeader
              isLoading={state.readStatus === states.PENDING}
              value={state.name}
              category={category}
              code={code}
            />
            )}
            <div className="aridhia-table-filter">
              {event && (<SubscriptionEventLabel event={event} className="dn" />)}
              <SubscribersFilters category={category} code={code} event={event} />
            </div>
          </TableHeader>
          <SubscribersTableColumns columns={columns} />
        </Table.Header>
        <Table.Body>
          <SubscribersTableRows items={items} columns={columns} />
        </Table.Body>
      </TableWrapper>
      <NoRowsWithPagination
        currentPage={pageNumber}
        entity="subscriber"
        goToPage={({ page }) => {
          fetchData({ event, page });
          ref.current.scrollIntoView();
        }}
        icon="user"
        items={items}
        pageCount={state.pageCount}
        readErrors={state.readErrors}
        readStatus={state.readStatus}
        total={state.total}
      />
    </div>
  );
};

export default SubscribersTable;
